import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ConfirmDialog from './ConfirmDialog';

const ConfirmContext = createContext(null);

/**
 * ConfirmProvider — monta un único ConfirmDialog en el árbol y expone
 * `useConfirm()` para pedirlo imperativamente desde cualquier componente.
 *
 * Uso:
 *   <ConfirmProvider>
 *     <App />
 *   </ConfirmProvider>
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, destructive });
 *   if (ok) { ... }
 */
export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, options: {} });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const close = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    const resolver = resolverRef.current;
    resolverRef.current = null;
    if (resolver) resolver(result);
  }, []);

  const handleConfirm = useCallback(() => close(true), [close]);
  const handleCancel = useCallback(() => close(false), [close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.options.title}
        message={state.options.message}
        confirmLabel={state.options.confirmLabel}
        cancelLabel={state.options.cancelLabel}
        destructive={state.options.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  }
  return ctx;
};

export default ConfirmProvider;
